// A horizontal scrollbar pinned to the bottom of the window, standing in for a scroll
// container's own bar while that bar is off-screen.
//
// A wide block that is also tall - a grid of cards several rows deep - puts its native
// horizontal scrollbar under the last row, which on a small screen is below the fold.
// Nothing tells the user the right-hand side is cut off. This mirrors the container's
// scrolling into a bar fixed at the bottom of the viewport, shown only while the
// container overflows, is on screen, and its own bar is not. When everything fits, or
// the real bar is in view, it is not there at all.
//
// StoneXGrid does the same thing for ag-grid with ag-grid-specific selectors; this is
// the same idea for any element.
import { RefObject, useEffect, useRef } from 'react';

const BAR_HEIGHT = 16;

export default function useFloatingHorizontalScrollbar<T extends HTMLElement>(containerRef: RefObject<T>) {
    const barRef = useRef<HTMLDivElement>(null);
    const innerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = containerRef.current;
        const bar = barRef.current;
        const inner = innerRef.current;
        if (!container || !bar || !inner) return;

        const update = () => {
            const overflows = container.scrollWidth > container.clientWidth + 1;
            const rect = container.getBoundingClientRect();
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

            const onScreen = rect.top < viewportHeight - BAR_HEIGHT && rect.bottom > 0;
            // The container's own scrollbar sits at its bottom edge; once that is in view
            // the floating one would just double it.
            const ownBarVisible = rect.bottom <= viewportHeight;

            const show = overflows && onScreen && !ownBarVisible;
            bar.style.display = show ? 'block' : 'none';
            if (!show) return;

            bar.style.left = `${rect.left}px`;
            bar.style.width = `${rect.width}px`;
            inner.style.width = `${container.scrollWidth}px`;
            if (bar.scrollLeft !== container.scrollLeft) bar.scrollLeft = container.scrollLeft;
        };

        // Setting scrollLeft to its current value fires no event, so mirroring in both
        // directions does not loop.
        const fromContainer = () => { bar.scrollLeft = container.scrollLeft; };
        const fromBar = () => { container.scrollLeft = bar.scrollLeft; };

        container.addEventListener('scroll', fromContainer, { passive: true });
        bar.addEventListener('scroll', fromBar, { passive: true });
        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);

        // The container changes width with the window and scrollWidth with its content.
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : undefined;
        observer?.observe(container);
        if (container.firstElementChild) observer?.observe(container.firstElementChild);

        update();

        return () => {
            container.removeEventListener('scroll', fromContainer);
            bar.removeEventListener('scroll', fromBar);
            window.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
            observer?.disconnect();
        };
    }, [containerRef]);

    return (
        <div
            ref={barRef}
            style={{
                display: 'none',
                position: 'fixed',
                bottom: 0,
                height: BAR_HEIGHT,
                overflowX: 'auto',
                overflowY: 'hidden',
                zIndex: 1200,
                backgroundColor: 'rgba(255, 255, 255, 0.85)',
            }}
        >
            <div ref={innerRef} style={{ height: 1 }} />
        </div>
    );
}
